import { SaleItem } from "./saleItem";
import { SocialMediaType } from "./socialMedia";

export interface FullProperty {
    id: string;
    name: string;
    location: GoogleLocation;
    addressName: string;
    isActive: boolean;
    isPublished: boolean;
    isAutoTradeEnabled: boolean;
    franchise: string;
    saleItems: SaleItem[];
    owner: string; // the customer.uid 
    staff: string[];
    incomingOrders: string[];
    ordersBeingPrepared: string[];
    ordersReadyForCollection: string[];
    ordersCompleted: string[];
    closingTime: string;
    mondayStart: string;
    mondayEnd: string;
    tuesdayStart: string;
    tuesdayEnd: string;
    wednesdayStart: string;
    wednesdayEnd: string;
    thursdayStart: string;
    thursdayEnd: string;
    fridayStart: string;
    fridayEnd: string;
    saturdayStart: string;
    saturdayEnd: string;
    sundayStart: string;
    sundayEnd: string;
    holidayStart: string;
    holidayEnd: string;

    offlineMonday: boolean;
    offlineTuesday: boolean;
    offlineWednesday: boolean;
    offlineThursday: boolean;
    offlineFriday: boolean;
    offlineSaturday: boolean;
    offlineSunday: boolean;
    offlineHoliday: boolean;

    startOfCollectionTimeDayDeltaFromNow?: number;
    endDateDayDeltaFromNow?: number;
    socialMedia: SocialMediaType[];
    enableCustomerSettlement?: boolean;
    // Hack: TODO Move this to a private secure collection
    totalSalesInZarC: number;
    totalRefundsInZarC: number;
    paymentHistory: Array<PaymentEvent>;
    isPlantBasedTreaty?: boolean; // New field to indicate if the property is part of the plant-based treaty
    category: string;
    isComingSoon: boolean;
}

export type PaymentEvent = {
    paymentDate: string,
    amount: number
}; // Date to amount paid in Zar Cents

interface GoogleLocation {
    lat: number;
    lng: number;
}


export const emptyProperty: FullProperty = {
    id: "",
    name: "",
    location: { lat: 0, lng: 0 },
    isActive: false,
    addressName: "",
    isPublished: false,
    isAutoTradeEnabled: false,
    franchise: "",
    saleItems: [],
    owner: "",
    staff: [],
    incomingOrders: [],
    ordersBeingPrepared: [],
    ordersReadyForCollection: [],
    ordersCompleted: [],
    closingTime: "",
    mondayStart: "",
    mondayEnd: "",
    tuesdayStart: "",
    tuesdayEnd: "",
    wednesdayStart: "",
    wednesdayEnd: "",
    thursdayStart: "",
    thursdayEnd: "",
    fridayStart: "",
    fridayEnd: "",
    saturdayStart: "",
    saturdayEnd: "",
    sundayStart: "",
    sundayEnd: "",
    startOfCollectionTimeDayDeltaFromNow: 0,
    endDateDayDeltaFromNow: 0,
    socialMedia: [],
    enableCustomerSettlement: false,
    totalSalesInZarC: 0,
    totalRefundsInZarC: 0,
    paymentHistory: [],
    holidayStart: "",
    holidayEnd: "",
    isPlantBasedTreaty: false,
    offlineMonday: false,
    offlineTuesday: false,
    offlineWednesday: false,
    offlineThursday: false,
    offlineFriday: false,
    offlineSaturday: false,
    offlineSunday: false,
    offlineHoliday: false,
    category: "",
    isComingSoon: false,
}

export const addMissingFullPropertyProperties = (part: Partial<FullProperty>): FullProperty => {
    // Spread in your “emptyProperty” defaults first,
    // then overwrite with whatever Firestore actually had.
    const complete: FullProperty = {
        ...emptyProperty,
        id: part.id ?? emptyProperty.id,
        name: part.name ?? emptyProperty.name,
        location: part.location ?? emptyProperty.location,
        isActive: part.isActive ?? emptyProperty.isActive,
        addressName: part.addressName ?? emptyProperty.addressName,
        isPublished: part.isPublished ?? emptyProperty.isPublished,
        isAutoTradeEnabled: part.isAutoTradeEnabled ?? emptyProperty.isAutoTradeEnabled,
        franchise: part.franchise ?? emptyProperty.franchise,
        saleItems: part.saleItems ?? emptyProperty.saleItems,
        owner: part.owner ?? emptyProperty.owner,
        staff: part.staff ?? emptyProperty.staff,
        incomingOrders: part.incomingOrders ?? emptyProperty.incomingOrders,
        ordersBeingPrepared: part.ordersBeingPrepared ?? emptyProperty.ordersBeingPrepared,
        ordersReadyForCollection: part.ordersReadyForCollection ?? emptyProperty.ordersReadyForCollection,
        ordersCompleted: part.ordersCompleted ?? emptyProperty.ordersCompleted,
        closingTime: part.closingTime ?? emptyProperty.closingTime,
        mondayStart: part.mondayStart ?? emptyProperty.mondayStart,
        mondayEnd: part.mondayEnd ?? emptyProperty.mondayEnd,
        tuesdayStart: part.tuesdayStart ?? emptyProperty.tuesdayStart,
        tuesdayEnd: part.tuesdayEnd ?? emptyProperty.tuesdayEnd,
        wednesdayStart: part.wednesdayStart ?? emptyProperty.wednesdayStart,
        wednesdayEnd: part.wednesdayEnd ?? emptyProperty.wednesdayEnd,
        thursdayStart: part.thursdayStart ?? emptyProperty.thursdayStart,
        thursdayEnd: part.thursdayEnd ?? emptyProperty.thursdayEnd,
        fridayStart: part.fridayStart ?? emptyProperty.fridayStart,
        fridayEnd: part.fridayEnd ?? emptyProperty.fridayEnd,
        saturdayStart: part.saturdayStart ?? emptyProperty.saturdayStart,
        saturdayEnd: part.saturdayEnd ?? emptyProperty.saturdayEnd,
        sundayStart: part.sundayStart ?? emptyProperty.sundayStart,
        sundayEnd: part.sundayEnd ?? emptyProperty.sundayEnd,
        startOfCollectionTimeDayDeltaFromNow: part.startOfCollectionTimeDayDeltaFromNow ?? emptyProperty.startOfCollectionTimeDayDeltaFromNow,
        endDateDayDeltaFromNow: part.endDateDayDeltaFromNow ?? emptyProperty.endDateDayDeltaFromNow,
        enableCustomerSettlement: part.enableCustomerSettlement ?? emptyProperty.enableCustomerSettlement,
        // your newly added field—use a default if missing
        socialMedia: part.socialMedia ?? emptyProperty.socialMedia,
        totalSalesInZarC: part.totalSalesInZarC ?? emptyProperty.totalSalesInZarC,
        totalRefundsInZarC: part.totalRefundsInZarC ?? emptyProperty.totalRefundsInZarC,
        paymentHistory: part.paymentHistory ?? emptyProperty.paymentHistory,
        holidayStart: part.holidayStart ?? emptyProperty.holidayStart,
        holidayEnd: part.holidayEnd ?? emptyProperty.holidayEnd,
        isPlantBasedTreaty: part.isPlantBasedTreaty ?? emptyProperty.isPlantBasedTreaty,

        offlineMonday: part.offlineMonday ?? emptyProperty.offlineMonday,
        offlineTuesday: part.offlineTuesday ?? emptyProperty.offlineTuesday,
        offlineWednesday: part.offlineWednesday ?? emptyProperty.offlineWednesday,
        offlineThursday: part.offlineThursday ?? emptyProperty.offlineThursday,
        offlineFriday: part.offlineFriday ?? emptyProperty.offlineFriday,
        offlineSaturday: part.offlineSaturday ?? emptyProperty.offlineSaturday,
        offlineSunday: part.offlineSunday ?? emptyProperty.offlineSunday,
        offlineHoliday: part.offlineHoliday ?? emptyProperty.offlineHoliday,
        category: part.category ?? emptyProperty.category,
        isComingSoon: part.isComingSoon ?? emptyProperty.isComingSoon,
      };

    return complete;
}